// The finishes a steam room's body is shown in, in the 3D view (docs/claude/woods.md,
// *Steam room finishes*). Each surface is a RECIPE for a generated texture
// (src/renderer/finishTextures.ts), its figures measured off a photograph of the
// material; a panel's finish also has its core, shown where the panel is cut
// across.

import type { RoomFinishes } from './types';

/** A finish's key, as stored in a drawing: never renamed (its name can be). */
export type FinishId = 'rough-body';

/** Cement: its base colour, brightened and darkened by noise at each scale
 *  (`[cell mm, standard deviation as a fraction]`) and by its `sand` — grains
 *  `size` mm across, each a brightness of deviation `amp`, a `dark` share of
 *  them `depth` × `amp` darker — with dark pores of each population (`perCm2`, a
 *  mean and a largest radius in mm, how much darker at the middle). */
export interface CementRecipe {
  kind: 'cement';
  seed: number;
  color: string;
  octaves: ReadonlyArray<readonly [number, number]>;
  sand: { size: number; amp: number; dark: number; depth: number };
  pores: ReadonlyArray<{ perCm2: number; radius: number; maxRadius: number; depth: number }>;
  /** Its average colour: the OBJ / 3D DXF exports' and the 3D view's where no
   *  texture can be made. */
  average: string;
}

/** EPS: beads `bead` mm apart on a grid jittered by `jitter` of a bead, each a
 *  disk `fill` of that across (± 15 %) trimmed to its Voronoi cell, from `rim` at
 *  its edge to `centre` at its middle, `spread` lighter or darker than the next;
 *  between them `gap`s `gapWidth` mm wide, darkening to `void` where they open
 *  up where three beads meet. */
export interface BeadsRecipe {
  kind: 'beads';
  seed: number;
  bead: number;
  jitter: number;
  fill: number;
  /** How far its corners are rounded, as a fraction of a bead. */
  round: number;
  centre: string;
  rim: string;
  gap: string;
  void: string;
  gapWidth: number;
  spread: number;
  average: string;
}

export type SurfaceRecipe = BeadsRecipe | CementRecipe;

export interface Finish {
  /** English: the DICT key its Italian and French names are looked up by. */
  name: string;
  /** Its face: a wall's inside and outside, the ceiling's underside, the roof. */
  face: SurfaceRecipe;
  /** A sandwich panel's core, where it is cut across (a jamb, a header's soffit,
   *  a sill, a wall's top), between skins of its face `skin` mm thick. */
  core?: { surface: SurfaceRecipe; skin: number };
}

/** Every finish, in the order the panel lists them. */
export const FINISHES: Readonly<Record<FinishId, Finish>> = {
  // Blue EPS between 2 mm of grey cement on each side, from the user's photograph
  // of a cut panel (white-balanced on its paper, its lighting flattened; the
  // beads taken as 5 mm, which sets every other size).
  'rough-body': {
    name: 'Rough body',
    face: {
      kind: 'cement', seed: 11, color: '#9da3a5',
      octaves: [[6, 0.017], [2.4, 0.03]],
      sand: { size: 0.6, amp: 0.055, dark: 0.07, depth: 3 },
      pores: [{ perCm2: 4, radius: 0.3, maxRadius: 0.9, depth: 0.4 }],
      average: '#9ba1a3',
    },
    core: {
      surface: {
        kind: 'beads', seed: 23, bead: 5, jitter: 0.38, fill: 0.74, round: 0.22,
        centre: '#adbfcc', rim: '#89a3b8', gap: '#6e99c1', void: '#41709d',
        gapWidth: 0.3, spread: 0.06, average: '#8da9c1',
      },
      skin: 2,
    },
  },
};

export const FINISH_IDS = Object.keys(FINISHES) as FinishId[];

/** What a steam room's body is in until it is given another finish. */
export const DEFAULT_FINISH: FinishId = 'rough-body';

/** The stored value for a part shown in its colour, with no finish. */
export const NO_FINISH = 'none';

/** The finish a stored key names, or null (none, or one this build lacks). */
export function finishOf(id: string | undefined): (Finish & { id: FinishId }) | null {
  return id && Object.prototype.hasOwnProperty.call(FINISHES, id) ? { ...FINISHES[id as FinishId], id: id as FinishId } : null;
}

/** A room its finishes dress: a walled steam room (a sauna is clad in woods). */
export function takesFinish(inst: { autoBenches?: boolean; category: string }): boolean {
  return !!inst.autoBenches && inst.category === 'steamroom';
}

/** The finish a part of a room is shown in, or null for its colour. A wall side
 *  with none stored (or one this build lacks) is the default; the ceiling with
 *  none follows the walls inside. */
export function roomFinishOf(f: RoomFinishes | undefined, part: keyof RoomFinishes): FinishId | null {
  const v = f?.[part];
  if (v === NO_FINISH) return null;
  if (part === 'ceiling' && !finishOf(v)) return roomFinishOf(f, 'wallsIn');
  return finishOf(v)?.id ?? DEFAULT_FINISH;
}

/** Give a part of a room a finish, `NO_FINISH` for its colour, or '' (the
 *  ceiling only) to follow the walls inside again. The default is stored as
 *  absent, and a room left with nothing stored drops `finishes`; an unknown
 *  finish changes nothing. */
export function setRoomFinish(inst: { finishes?: RoomFinishes }, part: keyof RoomFinishes, value: string): void {
  if (value !== NO_FINISH && value !== '' && !finishOf(value)) return;
  if (value === '' && part !== 'ceiling') return;
  const f: RoomFinishes = { ...inst.finishes };
  if (value === '' || (value === DEFAULT_FINISH && part !== 'ceiling')) delete f[part];
  else f[part] = value;
  if (Object.keys(f).length) inst.finishes = f;
  else delete inst.finishes;
}
